import axios from 'axios';
import { LoginCredentials, Token, CompanyRegistration, User, Company } from '../types/auth';
import { MachineWithShifts, MachineCreate, MachineUpdate } from '../types/machine';
import {
  Plant, PlantCreate, PlantUpdate,
  Department, DepartmentCreate, DepartmentUpdate,
  Shift, ShiftCreate, ShiftUpdate,
} from '../types/operations';
import { getFirebaseToken } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Firebase auth token
api.interceptors.request.use(
  async (config) => {
    // Try to get Firebase token first (preferred)
    const firebaseToken = await getFirebaseToken();
    if (firebaseToken) {
      config.headers.Authorization = `Bearer ${firebaseToken}`;
      return config;
    }

    // Fallback to legacy token from localStorage
    const legacyToken = localStorage.getItem('access_token');
    if (legacyToken) {
      config.headers.Authorization = `Bearer ${legacyToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear legacy token
      localStorage.removeItem('access_token');
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<Token> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  registerCompany: async (registration: CompanyRegistration): Promise<Company> => {
    const response = await api.post('/auth/register-company', registration);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const testAPI = {
  whoAmI: async () => {
    const response = await api.get('/whoami');
    return response.data;
  },
};

export interface MachineDataOverride {
  machine_id: number;
  good_qty?: number;
  scrap_qty?: number;
  downtime_min?: number;
}

export interface TestDataRequest {
  department_id?: number;
  shift_id?: number;
  date?: string;
  num_violations?: number;
  machine_overrides?: MachineDataOverride[];
}

export interface TestDataResponse {
  success: boolean;
  message: string;
  shift_report_id: number;
  alerts_created: number;
  machines_count: number;
}

export const simulatorAPI = {
  generateTestData: async (config: TestDataRequest = {}): Promise<TestDataResponse> => {
    const response = await api.post('/simulator/generate-test-data', config);
    return response.data;
  },
};

export const machineAPI = {
  /**
   * Get all machines for the current user's company
   */
  list: async (): Promise<MachineWithShifts[]> => {
    const response = await api.get('/machines');
    return response.data;
  },

  /**
   * Get a specific machine by ID
   */
  get: async (machineId: number): Promise<MachineWithShifts> => {
    const response = await api.get(`/machines/${machineId}`);
    return response.data;
  },

  /**
   * Create a new machine
   */
  create: async (machineData: MachineCreate): Promise<MachineWithShifts> => {
    const response = await api.post('/machines', machineData);
    return response.data;
  },

  /**
   * Update an existing machine
   */
  update: async (machineId: number, machineData: MachineUpdate): Promise<MachineWithShifts> => {
    const response = await api.put(`/machines/${machineId}`, machineData);
    return response.data;
  },

  /**
   * Delete (deactivate) a machine
   */
  delete: async (machineId: number): Promise<void> => {
    await api.delete(`/machines/${machineId}`);
  },
};

export const plantAPI = {
  list: async (activeOnly: boolean = true): Promise<Plant[]> => {
    const response = await api.get('/plants', { params: { active_only: activeOnly } });
    return response.data;
  },

  get: async (plantId: number): Promise<Plant> => {
    const response = await api.get(`/plants/${plantId}`);
    return response.data;
  },

  create: async (plantData: PlantCreate): Promise<Plant> => {
    const response = await api.post('/plants', plantData);
    return response.data;
  },

  update: async (plantId: number, plantData: PlantUpdate): Promise<Plant> => {
    const response = await api.put(`/plants/${plantId}`, plantData);
    return response.data;
  },

  delete: async (plantId: number, hardDelete: boolean = false): Promise<void> => {
    await api.delete(`/plants/${plantId}`, { params: { hard_delete: hardDelete } });
  },
};

export const departmentAPI = {
  list: async (plantId?: number, activeOnly: boolean = true): Promise<Department[]> => {
    const params: any = { active_only: activeOnly };
    if (plantId) params.plant_id = plantId;
    const response = await api.get('/departments', { params });
    return response.data;
  },

  get: async (departmentId: number): Promise<Department> => {
    const response = await api.get(`/departments/${departmentId}`);
    return response.data;
  },

  create: async (departmentData: DepartmentCreate): Promise<Department> => {
    const response = await api.post('/departments', departmentData);
    return response.data;
  },

  update: async (departmentId: number, departmentData: DepartmentUpdate): Promise<Department> => {
    const response = await api.put(`/departments/${departmentId}`, departmentData);
    return response.data;
  },

  delete: async (departmentId: number, hardDelete: boolean = false): Promise<void> => {
    await api.delete(`/departments/${departmentId}`, { params: { hard_delete: hardDelete } });
  },
};

export const shiftAPI = {
  list: async (departmentId?: number, plantId?: number, activeOnly: boolean = true): Promise<Shift[]> => {
    const params: any = { active_only: activeOnly };
    if (departmentId) params.department_id = departmentId;
    if (plantId) params.plant_id = plantId;
    const response = await api.get('/shifts', { params });
    return response.data;
  },

  get: async (shiftId: number): Promise<Shift> => {
    const response = await api.get(`/shifts/${shiftId}`);
    return response.data;
  },

  create: async (shiftData: ShiftCreate): Promise<Shift> => {
    const response = await api.post('/shifts', shiftData);
    return response.data;
  },

  update: async (shiftId: number, shiftData: ShiftUpdate): Promise<Shift> => {
    const response = await api.put(`/shifts/${shiftId}`, shiftData);
    return response.data;
  },

  delete: async (shiftId: number, hardDelete: boolean = false): Promise<void> => {
    await api.delete(`/shifts/${shiftId}`, { params: { hard_delete: hardDelete } });
  },
};


export interface MachineDataRecord {
  id: number;
  machine_id: number;  // Foreign key to machines table
  machine_identifier: string;  // Machine's string identifier (e.g., "CNC-001")
  machine_name: string | null;
  job_id: string | null;
  part_id: string | null;
  operator: string | null;
  good_qty: number;
  scrap_qty: number;
  downtime_min: number;
  production_date: string | null;
  shift_name: string | null;
}

export const machineDataAPI = {
  /**
   * Get all machine data for the current user's company
   */
  list: async (machineId?: string, productionDate?: string, shiftName?: string): Promise<MachineDataRecord[]> => {
    const params: any = {};
    if (machineId) params.machine_id = machineId;
    if (productionDate) params.production_date = productionDate;
    if (shiftName) params.shift_name = shiftName;
    const response = await api.get('/machine-data/', { params });
    return response.data;
  },

  /**
   * Get distinct shift names from machine data for filter dropdown
   */
  getShiftNames: async (dateFrom?: string, dateTo?: string): Promise<string[]> => {
    const params: any = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const response = await api.get('/machine-data/shift-names', { params });
    return response.data;
  },

  /**
   * Get specific machine data by ID
   */
  get: async (dataId: number): Promise<MachineDataRecord> => {
    const response = await api.get(`/machine-data/${dataId}`);
    return response.data;
  },
};

// ============================================================================
// OpActs - Task Management Types & API
// ============================================================================

export type TaskSeverity = 'high' | 'medium' | 'low';
export type TaskStatus = 'not_started' | 'in_progress' | 'completed' | 'reassigned';

export interface TaskCategory {
  id: number;
  name: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface TaskSummary {
  id: number;
  title: string;
  severity: TaskSeverity;
  status: TaskStatus;
  due_date: string | null;
  category_id: number;
  category_name: string | null;
  alert_id: number | null;
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;
  created_at: string;
}

export interface TaskDetail extends TaskSummary {
  company_id: number;
  instructions: string | null;
  assigned_at: string | null;
  created_by_user_id: number;
  completion_context: string | null;
  completed_at: string | null;
  is_active: boolean;
  updated_at: string | null;
  created_by_name: string | null;
}

export interface TaskCreate {
  title: string;
  instructions?: string;
  severity?: TaskSeverity;
  due_date?: string;
  category_id: number;
  alert_id?: number;
  assigned_to_user_id?: number;
}

export interface TaskUpdate {
  title?: string;
  instructions?: string;
  severity?: TaskSeverity;
  due_date?: string;
  category_id?: number;
  assigned_to_user_id?: number;
}

export interface TaskStatusUpdate {
  status: TaskStatus;
  completion_context?: string;
}

export interface TaskReassign {
  assigned_to_user_id: number;
  notes?: string;
}

export interface TaskFilters {
  category_id?: number;
  status?: TaskStatus;
  severity?: TaskSeverity;
  assigned_to_user_id?: number;
  alert_id?: number;
  due_date_from?: string;
  due_date_to?: string;
  include_completed?: boolean;
  skip?: number;
  limit?: number;
}

export interface TaskListResponse {
  items: TaskSummary[];
  total: number;
  skip: number;
  limit: number;
}

export interface TasksByCategoryCount {
  category_id: number;
  category_name: string;
  total: number;
  not_started: number;
  in_progress: number;
  completed: number;
  overdue: number;
}

export interface TaskDashboardSummary {
  total_tasks: number;
  overdue_tasks: number;
  due_today: number;
  categories: TasksByCategoryCount[];
}

export interface TaskHistoryEntry {
  id: number;
  task_id: number;
  change_type: 'created' | 'status_change' | 'reassigned' | 'field_update' | 'completed';
  old_status: string | null;
  new_status: string | null;
  old_assigned_to_user_id: number | null;
  new_assigned_to_user_id: number | null;
  old_assigned_to_name: string | null;
  new_assigned_to_name: string | null;
  changed_by_user_id: number;
  changed_by_name: string | null;
  notes: string | null;
  created_at: string;
}

export interface TaskDetailWithHistory extends TaskDetail {
  history: TaskHistoryEntry[];
}

// ============================================================================
// OpActs - Alert Management Types & API
// ============================================================================

export type AlertType = 'high_scrap' | 'high_downtime' | 'low_efficiency' | 'no_data';
export type AlertSeverity = 'critical' | 'warning' | 'info';
export type AlertStatus = 'unacknowledged' | 'acknowledged' | 'resolved';
export type NoActionReason = 'resolved_on_shift' | 'monitoring' | 'needs_review' | 'other';

export interface AlertSummary {
  id: number;
  alert_type: AlertType;
  severity: AlertSeverity;
  kpi_name: string;
  status: AlertStatus;
  department_id: number;
  machine_id: number | null;
  created_at: string;
}

export interface AlertDetail extends AlertSummary {
  company_id: number;
  shift_report_id: number | null;
  kpi_value: number | null;
  threshold_value: number | null;
  description: string | null;
  acknowledged_by_user_id: number | null;
  acknowledged_at: string | null;
  context: string | null;
  updated_at: string | null;
  // Action workflow fields
  action_needed: boolean | null;  // null = pending decision
  no_action_reason: NoActionReason | null;
  resolution_notes: string | null;
  // Joined fields
  machine_name: string | null;
  department_name: string | null;
  shift_name: string | null;
  acknowledged_by_name: string | null;
  task_count: number;
}

export interface AlertWithTasks extends AlertDetail {
  tasks: TaskSummary[];
}

export interface AlertAcknowledge {
  // Empty - quick ACK requires no data
}

export interface AlertActionDecision {
  action_needed: boolean;
  no_action_reason?: NoActionReason;
  resolution_notes?: string;
}

export interface AlertFilters {
  status?: AlertStatus;
  severity?: AlertSeverity;
  alert_type?: AlertType;
  department_id?: number;
  machine_id?: number;
  shift_name?: string;
  date_from?: string;
  date_to?: string;
  skip?: number;
  limit?: number;
}

export interface AlertListResponse {
  items: AlertDetail[];
  total: number;
  skip: number;
  limit: number;
}

export interface UnacknowledgedAlertCount {
  total: number;
  critical: number;
  warning: number;
}

export const alertAPI = {
  /**
   * Get count of unacknowledged alerts for dashboard badge
   */
  getUnacknowledgedCount: async (): Promise<UnacknowledgedAlertCount> => {
    const response = await api.get('/alerts/unacknowledged-count');
    return response.data;
  },

  /**
   * List alerts with filtering and pagination
   */
  list: async (filters?: AlertFilters): Promise<AlertListResponse> => {
    const response = await api.get('/alerts', { params: filters });
    return response.data;
  },

  /**
   * Get a single alert by ID
   */
  get: async (alertId: number): Promise<AlertDetail> => {
    const response = await api.get(`/alerts/${alertId}`);
    return response.data;
  },

  /**
   * Get alert with linked tasks
   */
  getWithTasks: async (alertId: number): Promise<AlertWithTasks> => {
    const response = await api.get(`/alerts/${alertId}/with-tasks`);
    return response.data;
  },

  /**
   * Quick acknowledge an alert (single tap - step 1)
   * Timestamps and records user, marks as "seen"
   */
  acknowledge: async (alertId: number): Promise<AlertDetail> => {
    const response = await api.post(`/alerts/${alertId}/acknowledge`);
    return response.data;
  },

  /**
   * Record action decision after acknowledgment (step 2)
   * If action_needed is false, resolves the alert
   */
  setActionDecision: async (alertId: number, data: AlertActionDecision): Promise<AlertDetail> => {
    const response = await api.post(`/alerts/${alertId}/action-decision`, data);
    return response.data;
  },

  /**
   * Mark an alert as resolved
   */
  resolve: async (alertId: number): Promise<AlertDetail> => {
    const response = await api.post(`/alerts/${alertId}/resolve`);
    return response.data;
  },
};

// User management types
export type UserRole = 'viewer' | 'supervisor' | 'admin';

export interface DepartmentBrief {
  id: number;
  name: string;
}

export interface UserDetail {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  is_company_admin: boolean;
  role: UserRole;
  company_id: number;
  created_at: string;
  last_login: string | null;
}

export interface UserInvite {
  email: string;
  full_name: string;
  role: UserRole;
}

export interface UserUpdateAdmin {
  full_name?: string;
  role?: UserRole;
  is_active?: boolean;
}

export const userAPI = {
  /**
   * List all users in the current company
   */
  list: async (params?: { assignable_only?: boolean }): Promise<UserDetail[]> => {
    const response = await api.get('/users/', { params });
    return response.data;
  },

  /**
   * Get a single user by ID
   */
  get: async (userId: number): Promise<UserDetail> => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  /**
   * Invite a new user (creates Firebase account + DB record + sends invitation)
   */
  invite: async (data: UserInvite): Promise<UserDetail & { warning?: string }> => {
    const response = await api.post('/users/invite', data);
    return response.data;
  },

  /**
   * Update user (admin: role, departments, status; self: name only)
   */
  update: async (userId: number, data: UserUpdateAdmin): Promise<UserDetail> => {
    const response = await api.put(`/users/${userId}`, data);
    return response.data;
  },

  /**
   * Deactivate a user
   */
  deactivate: async (userId: number): Promise<UserDetail> => {
    const response = await api.post(`/users/${userId}/deactivate`);
    return response.data;
  },

  /**
   * Reactivate a deactivated user
   */
  reactivate: async (userId: number): Promise<UserDetail> => {
    const response = await api.post(`/users/${userId}/reactivate`);
    return response.data;
  },

  /**
   * Delete a user permanently
   */
  delete: async (userId: number): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },
};

// ============================================================================
// Company Settings Types & API
// ============================================================================

export type PassdownDeliveryMode = 'email_only' | 'assignment_only' | 'email_and_assignment';

export interface CompanySettings {
  passdown_delivery_mode: PassdownDeliveryMode;
}

export const companyAPI = {
  /**
   * Get company settings for current user's company
   */
  getSettings: async (): Promise<CompanySettings> => {
    const response = await api.get('/companies/settings');
    return response.data;
  },

  /**
   * Update company settings (admin only)
   */
  updateSettings: async (settings: CompanySettings): Promise<CompanySettings> => {
    const response = await api.put('/companies/settings', settings);
    return response.data;
  },
};

export const taskAPI = {
  /**
   * Get all task categories
   */
  getCategories: async (): Promise<TaskCategory[]> => {
    const response = await api.get('/tasks/categories');
    return response.data;
  },

  /**
   * Get dashboard summary counts by category
   */
  getDashboardSummary: async (): Promise<TaskDashboardSummary> => {
    const response = await api.get('/tasks/dashboard-summary');
    return response.data;
  },

  /**
   * List tasks with filtering and pagination
   */
  list: async (filters?: TaskFilters): Promise<TaskListResponse> => {
    const response = await api.get('/tasks', { params: filters });
    return response.data;
  },

  /**
   * Get a single task by ID
   */
  get: async (taskId: number): Promise<TaskDetail> => {
    const response = await api.get(`/tasks/${taskId}`);
    return response.data;
  },

  /**
   * Create a new task
   */
  create: async (taskData: TaskCreate): Promise<TaskDetail> => {
    const response = await api.post('/tasks', taskData);
    return response.data;
  },

  /**
   * Update task fields
   */
  update: async (taskId: number, taskData: TaskUpdate): Promise<TaskDetail> => {
    const response = await api.put(`/tasks/${taskId}`, taskData);
    return response.data;
  },

  /**
   * Update task status (with required context for completion)
   */
  updateStatus: async (taskId: number, statusData: TaskStatusUpdate): Promise<TaskDetail> => {
    const response = await api.patch(`/tasks/${taskId}/status`, statusData);
    return response.data;
  },

  /**
   * Reassign task to a different user
   */
  reassign: async (taskId: number, reassignData: TaskReassign): Promise<TaskDetail> => {
    const response = await api.patch(`/tasks/${taskId}/reassign`, reassignData);
    return response.data;
  },

  /**
   * Get a task with full audit history
   */
  getWithHistory: async (taskId: number): Promise<TaskDetailWithHistory> => {
    const response = await api.get(`/tasks/${taskId}/history`);
    return response.data;
  },

  /**
   * Soft delete a task
   */
  delete: async (taskId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}`);
  },
};

// ============================================================================
// OpActs - Passdown Management Types & API
// ============================================================================

// Passdown status values
export type PassdownStatus = 'pending' | 'acknowledged' | 'in_progress' | 'completed' | 'overdue';

// Passdown reason values
export type PassdownReason = 'shift_end' | 'break' | 'emergency' | 'reassignment' | 'other';

export interface KPISummary {
  total_good_qty: number;
  total_scrap_qty: number;
  scrap_percentage: number;
  total_downtime_min: number;
  // null when no runtime data is available to compute efficiency
  efficiency_percentage: number | null;
  machines_reporting: number;
  machines_missing_data: number;
}

export interface MachineKPI {
  machine_id: number;
  machine_identifier: string;
  machine_name: string;
  good_qty: number;
  scrap_qty: number;
  scrap_percentage: number;
  downtime_min: number;
  // null when the machine has no runtime data to compute efficiency
  efficiency_percentage: number | null;
  has_data: boolean;
}

export interface PassdownPreviewAlert {
  id: number;
  alert_type: string;
  severity: string;
  kpi_name: string;
  kpi_value: number | null;
  threshold_value: number | null;
  description: string | null;
  status: string;
  context: string | null;
  created_at: string | null;
  machine_id: number | null;
  machine_identifier: string | null;
  machine_name: string | null;
  action_needed: boolean | null;
  task_count: number;
}

export interface PassdownPreviewTask {
  id: number;
  title: string;
  severity: string;
  status: string;
  due_date: string | null;
  assigned_to_name: string | null;
}

export interface PassdownPreview {
  shift_date: string;
  shift_name: string;
  department_id: number;
  department_name: string;
  kpi_summary: KPISummary;
  machine_kpis: MachineKPI[];
  alerts_total: number;
  alerts_critical: number;
  alerts_warning: number;
  alerts_acknowledged: number;
  alerts: PassdownPreviewAlert[];
  tasks_open: number;
  tasks_by_category: Record<string, PassdownPreviewTask[]>;
  tasks_completed_today: number;
  completed_tasks: { id: number; title: string; category_name: string | null; completion_context: string | null; completed_at: string | null }[];
}

export interface PassdownCreate {
  department_id: number;
  shift_report_id?: number;
  shift_date: string;
  shift_name: string;
  include_completed_tasks?: boolean;
  supervisor_notes?: string;
  // Accountability fields
  assigned_to_user_id?: number;
  reason?: PassdownReason;
  summary?: string;
  tasks_completed?: string;
  next_actions?: string;
  sla_deadline?: string;  // ISO datetime string
}

export interface PassdownDetail {
  id: number;
  company_id: number;
  department_id: number;
  shift_report_id: number | null;
  shift_date: string;
  shift_name: string;
  // Accountability fields
  assigned_to_user_id: number | null;
  reason: PassdownReason;
  status: PassdownStatus;
  summary: string | null;
  tasks_completed: string | null;
  next_actions: string | null;
  sla_deadline: string | null;
  acknowledged_at: string | null;
  acknowledged_by_user_id: number | null;
  // KPI and counts
  kpi_summary: Record<string, any> | null;
  alerts_total: number;
  alerts_critical: number;
  alerts_acknowledged: number;
  tasks_open: number;
  tasks_completed_today: number;
  include_completed_tasks: boolean;
  // Email tracking
  email_sent_at: string | null;
  email_sent_to: string[] | null;
  email_error: string | null;
  supervisor_notes: string | null;
  // Creator
  created_by_user_id: number;
  created_at: string;
  updated_at: string | null;
  // Nested names
  department_name: string | null;
  created_by_name: string | null;
  assigned_to_name: string | null;
  acknowledged_by_name: string | null;
  // Computed
  is_overdue: boolean;
}

export interface PassdownSummary {
  id: number;
  shift_date: string;
  shift_name: string;
  department_id: number;
  department_name: string | null;
  // Accountability status
  status: PassdownStatus;
  reason: PassdownReason;
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;
  sla_deadline: string | null;
  is_overdue: boolean;
  // Counts
  alerts_total: number;
  alerts_critical: number;
  tasks_open: number;
  email_sent_at: string | null;
  created_at: string;
}

export interface PassdownFilters {
  department_id?: number;
  date_from?: string;
  date_to?: string;
  skip?: number;
  limit?: number;
}

export interface PassdownListResponse {
  items: PassdownSummary[];
  total: number;
  skip: number;
  limit: number;
}

export interface PassdownSendRequest {
  recipient_emails?: string[];
}

export interface PassdownSendResult {
  success: boolean;
  error: string | null;
  recipients_count: number;
  sent_to?: string[];
  message?: string;
}

export interface PassdownAcknowledge {
  notes?: string;
}

export interface PassdownStatusUpdate {
  status: PassdownStatus;
  notes?: string;
}

export interface PassdownPendingCount {
  pending_count: number;
  overdue_count: number;
}

export interface PassdownPendingResponse {
  items: PassdownSummary[];
  total: number;
}

export const passdownAPI = {
  /**
   * Preview passdown data before creating/sending
   */
  preview: async (
    departmentId: number,
    shiftDate: string,
    shiftName: string,
    includeCompletedTasks: boolean = false,
    shiftReportId?: number
  ): Promise<PassdownPreview> => {
    const params: any = {
      department_id: departmentId,
      shift_date: shiftDate,
      shift_name: shiftName,
      include_completed_tasks: includeCompletedTasks,
    };
    if (shiftReportId) params.shift_report_id = shiftReportId;
    const response = await api.get('/passdowns/preview', { params });
    return response.data;
  },

  /**
   * List passdowns with filtering and pagination
   */
  list: async (filters?: PassdownFilters): Promise<PassdownListResponse> => {
    const response = await api.get('/passdowns', { params: filters });
    return response.data;
  },

  /**
   * Get a single passdown by ID
   */
  get: async (passdownId: number): Promise<PassdownDetail> => {
    const response = await api.get(`/passdowns/${passdownId}`);
    return response.data;
  },

  /**
   * Create a new passdown record
   */
  create: async (passdownData: PassdownCreate): Promise<PassdownDetail> => {
    const response = await api.post('/passdowns', passdownData);
    return response.data;
  },

  /**
   * Send passdown email to recipients
   */
  send: async (passdownId: number, data?: PassdownSendRequest): Promise<PassdownSendResult> => {
    const response = await api.post(`/passdowns/${passdownId}/send`, data || {});
    return response.data;
  },

  /**
   * Get passdowns assigned to current user that are pending acknowledgment
   */
  getMyPending: async (): Promise<PassdownPendingResponse> => {
    const response = await api.get('/passdowns/my-pending');
    return response.data;
  },

  /**
   * Get count of pending passdowns for current user (for badge display)
   */
  getPendingCount: async (): Promise<PassdownPendingCount> => {
    const response = await api.get('/passdowns/pending-count');
    return response.data;
  },

  /**
   * Acknowledge a passdown assigned to current user
   */
  acknowledge: async (passdownId: number, data?: PassdownAcknowledge): Promise<PassdownDetail> => {
    const response = await api.post(`/passdowns/${passdownId}/acknowledge`, data || {});
    return response.data;
  },

  /**
   * Update passdown status (acknowledged -> in_progress -> completed)
   */
  updateStatus: async (passdownId: number, data: PassdownStatusUpdate): Promise<PassdownDetail> => {
    const response = await api.patch(`/passdowns/${passdownId}/status`, data);
    return response.data;
  },
};

// ============================================================================
// Report Recipients Types & API
// ============================================================================

export interface ReportRecipient {
  id: number;
  company_id: number;
  email: string;
  name: string | null;
  role: string | null;
  plant_id: number | null;
  department_id: number | null;
  shift_id: number | null;
  is_active: boolean;
  receive_pdf: boolean;
  receive_summary_only: boolean;
  created_at: string;
  updated_at: string | null;
  plant_name: string | null;
  department_name: string | null;
  shift_name: string | null;
}

export interface ReportRecipientCreate {
  email: string;
  name?: string;
  role?: string;
  plant_id?: number | null;
  department_id?: number | null;
  shift_id?: number | null;
  is_active?: boolean;
  receive_pdf?: boolean;
  receive_summary_only?: boolean;
}

export interface ReportRecipientUpdate {
  email?: string;
  name?: string;
  role?: string;
  plant_id?: number | null;
  department_id?: number | null;
  shift_id?: number | null;
  is_active?: boolean;
  receive_pdf?: boolean;
  receive_summary_only?: boolean;
}

export const reportRecipientAPI = {
  list: async (params?: { plant_id?: number; department_id?: number; shift_id?: number; active_only?: boolean }): Promise<ReportRecipient[]> => {
    const response = await api.get('/report-recipients/', { params });
    return response.data;
  },

  create: async (data: ReportRecipientCreate): Promise<ReportRecipient> => {
    const response = await api.post('/report-recipients/', data);
    return response.data;
  },

  update: async (recipientId: number, data: ReportRecipientUpdate): Promise<ReportRecipient> => {
    const response = await api.put(`/report-recipients/${recipientId}`, data);
    return response.data;
  },

  delete: async (recipientId: number): Promise<void> => {
    await api.delete(`/report-recipients/${recipientId}`);
  },
};

// Unified API object for easier imports
export const apiService = {
  auth: authAPI,
  test: testAPI,
  machines: machineAPI,
  machineData: machineDataAPI,
  plants: plantAPI,
  departments: departmentAPI,
  shifts: shiftAPI,
  users: userAPI,
  company: companyAPI,
  alerts: alertAPI,
  tasks: taskAPI,
  passdowns: passdownAPI,
  reportRecipients: reportRecipientAPI,
};

export default api;
